
import React, { useState, useEffect } from 'react';
import { ShieldCheck, Plus, Mail, MoreHorizontal, UserCheck, Star, Activity, Settings, X, Lock, Unlock, FileText, User as UserIcon, Save, UserPlus, Loader2, Clock, Calendar, Send, ToggleLeft, ToggleRight, AlertTriangle, Check, Trash2, UserMinus, MessageSquare } from 'lucide-react';
import { User, UserRole, UserPermissions, SessionLog, Invitation, Container } from '../types';
import { supabase } from '../supabase';

interface TeamListProps {
  users: User[];
  currentUser: User;
  sessionLogs: SessionLog[];
  onUpdatePermissions: (userId: string, permissions: UserPermissions) => void;
  onUpdateUser: (userId: string, updates: Partial<User>) => void;
  onCreateUser: (user: Omit<User, 'id'>) => Promise<void>;
  container?: Container | null;
}

const TeamList: React.FC<TeamListProps> = ({ users, currentUser, sessionLogs, onUpdatePermissions, onUpdateUser, onCreateUser, container }) => {
  const [editingUserId, setEditingUserId] = useState<string | null>(null);
  const [viewingLogsUserId, setViewingLogsUserId] = useState<string | null>(null);
  const [removingUserId, setRemovingUserId] = useState<string | null>(null);
  const [isInvitingMember, setIsInvitingMember] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [pendingRequests, setPendingRequests] = useState<Invitation[]>([]);
  const [sendingTasksUserId, setSendingTasksUserId] = useState<string | null>(null);
  const [sendTasksResult, setSendTasksResult] = useState<{ userId: string; msg: string } | null>(null);
  
  // Estado local para edição de permissões
  const [permissionsForm, setPermissionsForm] = useState<UserPermissions | null>(null);
  
  const [inviteForm, setInviteForm] = useState({
    email: '',
    role: UserRole.EDITOR
  });

  const isAdmin = currentUser.role === UserRole.ADMIN || currentUser.role === UserRole.SUPERADMIN;

  useEffect(() => {
    if (isAdmin && container?.id) {
      fetchPendingRequests();
    }
  }, [isAdmin, container?.id]);

  const fetchPendingRequests = async () => {
    if (!container?.id) return;
    const { data } = await supabase
      .from('invitations')
      .select('*')
      .eq('container_id', container.id)
      .eq('status', 'request');
    if (data) setPendingRequests(data);
  };

  // Carrega permissões ao abrir modal de edição
  useEffect(() => {
    if (editingUserId) {
      const user = users.find(u => u.id === editingUserId);
      if (user) {
        setPermissionsForm(user.permissions);
      }
    }
  }, [editingUserId, users]);

  const handleInviteMember = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteForm.email || !container?.id) return;

    setLoading(true);
    try {
      const { error } = await supabase.from('invitations').insert([{
        email: inviteForm.email.toLowerCase().trim(),
        role: inviteForm.role,
        container_id: container.id,
        status: 'pending'
      }]);

      if (error) throw error;

      alert(`Convite enviado para ${inviteForm.email}`);
      setIsInvitingMember(false);
      setInviteForm({ email: '', role: UserRole.EDITOR });
    } catch (err: any) {
      console.error("Erro ao convidar:", err);
      alert("Erro ao convidar: " + (err.message || "Violação de política de segurança (RLS)"));
    } finally {
      setLoading(false);
    }
  };

  const handleApproveRequest = async (invite: Invitation) => {
    if (!confirm(`Aprovar entrada de ${invite.email}?`) || !container?.id) return;
    setLoading(true);
    try {
      const { data: profileData, error: profileFindError } = await supabase
        .from('profiles')
        .select('id')
        .eq('email', invite.email)
        .single();

      if (profileFindError || !profileData) throw new Error("Perfil do usuário não encontrado.");

      const { error: updateError } = await supabase
        .from('profiles')
        .update({ container_id: container.id })
        .eq('id', profileData.id);

      if (updateError) throw updateError;

      await supabase.from('invitations').delete().eq('id', invite.id);
      
      alert("Usuário aprovado com sucesso!");
      fetchPendingRequests();
      window.location.reload(); 

    } catch (err: any) {
      alert("Erro ao aprovar: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSavePermissions = async () => {
    if (editingUserId && permissionsForm) {
      setLoading(true);
      setErrorMsg(null);
      try {
        await onUpdateUser(editingUserId, { permissions: permissionsForm });
        setEditingUserId(null);
      } catch(err: any) {
        console.error(err);
        setErrorMsg("Erro ao salvar permissões. Verifique as políticas de banco.");
      } finally {
        setLoading(false);
      }
    }
  };

  const handleConfirmRemoveMember = async () => {
    if (!removingUserId) return;
    setLoading(true);
    setErrorMsg(null);
    try {
      // Ao definir container_id como null, o usuário é desconectado do gabinete
      // e redirecionado para a tela de onboarding ao recarregar o app.
      const { error } = await supabase
        .from('profiles')
        .update({ 
          container_id: null,
          role: UserRole.EDITOR 
        })
        .eq('id', removingUserId);

      if (error) throw error;

      setRemovingUserId(null);
      // Força refresh dos usuários locais
      window.location.reload();
    } catch (err: any) {
      console.error(err);
      setErrorMsg(err.message || "Erro 403: O banco bloqueou esta ação. Verifique o RLS.");
    } finally {
      setLoading(false);
    }
  };

  const handleSendAllTasks = async (userId: string) => {
    if (!container?.id) return;
    setSendingTasksUserId(userId);
    setSendTasksResult(null);
    try {
      const { data, error } = await supabase.functions.invoke('whatsapp-notify', {
        body: { event: 'send_all_tasks', container_id: container.id, target_user_id: userId },
      });
      if (error) {
        setSendTasksResult({ userId, msg: error.message || 'Erro ao enviar' });
      } else if (data?.error) {
        setSendTasksResult({ userId, msg: data.error });
      } else {
        const count = data?.taskCount || 0;
        setSendTasksResult({ userId, msg: count > 0 ? `${count} tarefas enviadas!` : 'Sem tarefas pendentes' });
      }
    } catch (err: any) {
      setSendTasksResult({ userId, msg: err.message || 'Erro' });
    } finally {
      setSendingTasksUserId(null);
      setTimeout(() => setSendTasksResult(null), 4000);
    }
  };

  const togglePermission = (key: keyof UserPermissions) => {
    if (permissionsForm) {
      setPermissionsForm({ ...permissionsForm, [key]: !permissionsForm[key] });
    }
  };

  const getUserLogs = (userId: string) => {
    return sessionLogs.filter(log => log.user_id === userId).slice(0, 15);
  };

  const editingUser = users.find(u => u.id === editingUserId);
  const removingUser = users.find(u => u.id === removingUserId);

  return (
    <div className="space-y-8 animate-in fade-in duration-500 pb-12">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-3xl font-black text-navy-950 dark:text-white tracking-tight">Equipe do Gabinete</h2>
          <p className="text-slate-500 dark:text-slate-400 font-medium mt-1">Gestão de acessos, permissões e auditoria de login.</p>
        </div>
        {isAdmin && container?.id && (
          <div className="flex gap-2">
              <button 
                onClick={() => setIsInvitingMember(true)}
                className="bg-navy-950 dark:bg-parliament-600 text-white px-8 py-4 rounded-full font-bold shadow-lg flex items-center gap-3 transition-all text-[10px] uppercase tracking-widest"
              >
                <UserPlus size={18} /> Convidar Membro
              </button>
          </div>
        )}
      </div>

      {/* Solicitações Pendentes */}
      {pendingRequests.length > 0 && (
        <div className="bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800/30 rounded-[32px] p-8 animate-in slide-in-from-top-4">
           <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-amber-100 dark:bg-amber-900/30 text-amber-600 rounded-xl"><UserPlus size={20}/></div>
              <h3 className="text-xl font-black text-amber-900 dark:text-amber-100">Solicitações de Entrada</h3>
           </div>
           <div className="space-y-3">
              {pendingRequests.map(req => (
                 <div key={req.id} className="flex items-center justify-between bg-white dark:bg-navy-900 p-4 rounded-2xl border border-amber-100 dark:border-amber-800/50 shadow-sm">
                    <div>
                       <p className="text-sm font-bold text-navy-950 dark:text-white">{req.email}</p>
                       <p className="text-[10px] font-black uppercase text-amber-500 tracking-widest mt-0.5">Solicitando como {req.role}</p>
                    </div>
                    <div className="flex gap-2">
                       <button 
                         onClick={async () => {
                            if(confirm("Rejeitar solicitação?")) {
                               await supabase.from('invitations').delete().eq('id', req.id);
                               fetchPendingRequests();
                            }
                         }}
                         className="px-4 py-2 text-[10px] font-black uppercase text-slate-400 hover:text-red-500"
                       >
                          Rejeitar
                       </button>
                       <button 
                         onClick={() => handleApproveRequest(req)}
                         disabled={loading}
                         className="px-6 py-2 bg-amber-500 text-white rounded-xl text-[10px] font-black uppercase shadow-lg hover:bg-amber-600 transition-all flex items-center gap-2"
                       >
                          {loading ? <Loader2 className="animate-spin" size={14}/> : <Check size={14}/>} Aprovar
                       </button>
                    </div>
                 </div>
              ))}
           </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {users.map(u => (
          <div key={u.id} className="bg-white dark:bg-navy-900 rounded-[32px] border border-neutral-200 dark:border-navy-800 shadow-sm p-8 hover:shadow-xl transition-all group">
            <div className="flex justify-between items-start mb-6">
              <div className="w-16 h-16 rounded-3xl bg-parliament-50 dark:bg-navy-800 text-parliament-600 dark:text-white flex items-center justify-center text-2xl font-black shadow-inner">
                {u.name.charAt(0)}
              </div>
              <div className={`px-4 py-1.5 rounded-full text-[9px] font-black uppercase tracking-widest border ${u.role === UserRole.ADMIN ? 'bg-red-50 text-red-600 border-red-100 dark:bg-red-900/20 dark:border-red-900/30' : u.role === UserRole.LEADERSHIP ? 'bg-indigo-50 text-indigo-600 border-indigo-100 dark:bg-indigo-900/20' : 'bg-parliament-50 text-parliament-600 border-parliament-100 dark:bg-navy-800 dark:text-slate-300 dark:border-navy-700'}`}>
                {u.role === UserRole.LEADERSHIP ? 'LIDERANÇA' : u.role}
              </div>
            </div>

            <div className="space-y-1 mb-6">
              <h3 className="text-xl font-black text-navy-950 dark:text-white flex items-center gap-2">
                {u.name}
                {u.role === UserRole.ADMIN && <Star size={16} className="text-parliament-600 fill-parliament-600" />}
              </h3>
              <p className="text-xs font-bold text-slate-400">{u.email}</p>
            </div>

            <div className="space-y-4 pt-6 border-t border-neutral-100 dark:border-navy-800">
              <p className="text-[9px] font-black text-slate-300 uppercase tracking-widest">Permissões Ativas</p>
              <div className="flex flex-wrap gap-2">
                {Object.entries(u.permissions).map(([key, val]) => {
                  if (!val || key === 'canEditOwnContent') return null;
                  let label = key.replace('canEdit', '');
                  if(label === 'CRM') label = 'Contatos';
                  if(label === 'Attendances') label = 'Atendimentos';
                  if(label === 'Tasks') label = 'Tarefas';
                  if(label === 'Letters') label = 'Documentos';
                  
                  return (
                    <span key={key} className="px-3 py-1 bg-neutral-50 dark:bg-navy-950 text-slate-600 dark:text-slate-400 rounded-full text-[9px] font-bold border border-neutral-100 dark:border-navy-800 uppercase">
                      {label}
                    </span>
                  )
                })}
                {u.permissions.canEditOwnContent && u.role === UserRole.LEADERSHIP && (
                   <span className="px-3 py-1 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400 rounded-full text-[9px] font-bold border border-emerald-100 uppercase">
                      Edição Própria
                   </span>
                )}
                {u.permissions.canAccessWhatsApp && (
                   <span className="px-3 py-1 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400 rounded-full text-[9px] font-bold border border-emerald-100 uppercase">
                      WhatsApp
                   </span>
                )}
              </div>
            </div>

            {sendTasksResult?.userId === u.id && (
              <div className={`mt-4 px-4 py-2 rounded-xl text-[10px] font-bold text-center ${sendTasksResult.msg.includes('enviadas') ? 'bg-emerald-50 text-emerald-600 dark:bg-emerald-900/20 dark:text-emerald-400' : 'bg-amber-50 text-amber-600 dark:bg-amber-900/20 dark:text-amber-400'}`}>
                {sendTasksResult.msg}
              </div>
            )}

            <div className="flex gap-2 mt-8">
               <button
                 onClick={() => setViewingLogsUserId(u.id)}
                 className="flex-1 py-3 bg-neutral-50 dark:bg-navy-950 text-slate-600 dark:text-slate-400 rounded-2xl font-black text-[9px] uppercase tracking-widest hover:bg-neutral-100 dark:hover:bg-navy-800 transition-all flex items-center justify-center gap-2"
               >
                 <Activity size={14} /> Logs
               </button>
               {u.phone && isAdmin && (
                 <button
                   onClick={() => handleSendAllTasks(u.id)}
                   disabled={sendingTasksUserId === u.id}
                   className="py-3 px-4 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400 rounded-2xl font-black text-[9px] uppercase tracking-widest hover:bg-emerald-100 dark:hover:bg-emerald-900/40 transition-all flex items-center justify-center gap-2"
                   title="Enviar tarefas via WhatsApp"
                 >
                   {sendingTasksUserId === u.id ? <Loader2 size={14} className="animate-spin" /> : <MessageSquare size={14} />} Tarefas
                 </button>
               )}
               {isAdmin && u.id !== currentUser.id && (
                  <>
                    <button
                      onClick={() => setEditingUserId(u.id)}
                      className="flex-1 py-3 bg-parliament-50 dark:bg-navy-800 text-parliament-600 dark:text-white rounded-2xl font-black text-[9px] uppercase tracking-widest hover:bg-parliament-100 dark:hover:bg-navy-700 transition-all flex items-center justify-center gap-2"
                    >
                      <Settings size={14} /> Configurar
                    </button>
                    <button
                      onClick={() => setRemovingUserId(u.id)}
                      className="p-3 text-slate-300 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-950/20 rounded-2xl transition-all"
                      title="Remover da equipe"
                    >
                      <UserMinus size={18} />
                    </button>
                  </>
               )}
            </div>
          </div>
        ))}
      </div>

      {/* MODAL DE REMOÇÃO DE MEMBRO */}
      {removingUserId && removingUser && (
        <div className="fixed inset-0 z-[150] bg-navy-950/80 backdrop-blur-sm flex flex-col items-center justify-center p-8 text-center animate-in fade-in zoom-in-95" onClick={() => !loading && setRemovingUserId(null)}>
           <div className="w-full max-w-md bg-white dark:bg-navy-900 p-10 rounded-[32px] border border-neutral-200 dark:border-navy-800 shadow-2xl" onClick={e => e.stopPropagation()}>
             <div className="w-20 h-20 bg-red-50 dark:bg-red-900/20 rounded-[24px] flex items-center justify-center mb-6 shadow-xl mx-auto text-red-500">
               <UserMinus size={36} />
             </div>
             <h3 className="text-3xl font-black text-navy-950 dark:text-white tracking-tighter mb-2">Remover da Equipe?</h3>
             <p className="text-slate-500 dark:text-slate-400 font-medium mb-6 leading-relaxed">
               Deseja remover <strong>{removingUser.name}</strong> deste gabinete? O acesso do usuário será revogado imediatamente.
             </p>
             
             {errorMsg && (
                <div className="mb-6 p-4 bg-red-50 text-red-600 rounded-2xl text-[10px] font-black uppercase flex items-center gap-2 border border-red-100">
                   <AlertTriangle size={14}/> {errorMsg}
                </div>
             )}

             <div className="flex gap-4 justify-center">
                <button disabled={loading} onClick={() => setRemovingUserId(null)} className="flex-1 py-4 bg-neutral-100 dark:bg-navy-800 text-slate-600 dark:text-slate-300 rounded-xl font-black text-[10px] uppercase tracking-widest hover:bg-neutral-200 transition-all">Cancelar</button>
                <button 
                  onClick={handleConfirmRemoveMember} 
                  disabled={loading}
                  className="flex-1 py-4 bg-red-600 text-white rounded-xl font-black text-[10px] uppercase tracking-widest shadow-xl hover:bg-red-700 transition-all flex items-center justify-center gap-2"
                >
                   {loading ? <Loader2 size={16} className="animate-spin"/> : 'Confirmar Remoção'}
                </button>
             </div>
           </div>
        </div>
      )}

      {/* MODAL DE EDIÇÃO DE PERMISSÕES */}
      {editingUserId && editingUser && permissionsForm && (
        <div className="fixed inset-0 z-[130] flex items-center justify-center p-6 bg-navy-950/60 backdrop-blur-sm animate-in fade-in" onClick={() => !loading && setEditingUserId(null)}>
           <div className="bg-white dark:bg-navy-900 w-full max-w-lg rounded-[32px] shadow-2xl overflow-hidden border border-neutral-200 dark:border-navy-800 animate-in zoom-in-95" onClick={e => e.stopPropagation()}>
              <div className="p-8 border-b border-neutral-100 dark:border-navy-800 flex justify-between items-center bg-neutral-50 dark:bg-navy-950">
                 <div>
                    <h3 className="text-xl font-black text-navy-950 dark:text-white">Ajustes de Acesso</h3>
                    <p className="text-xs font-medium text-slate-500 mt-1">Usuário: {editingUser.name}</p>
                 </div>
                 <button onClick={() => setEditingUserId(null)} className="p-2 hover:bg-neutral-200 dark:hover:bg-navy-800 rounded-full text-slate-400"><X size={20}/></button>
              </div>
              
              <div className="p-8 space-y-6">
                 {errorMsg && (
                    <div className="p-4 bg-red-50 text-red-600 rounded-2xl text-[10px] font-black uppercase flex items-center gap-2">
                       <AlertTriangle size={14}/> {errorMsg}
                    </div>
                 )}

                 <div className="space-y-2">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Telefone / WhatsApp</label>
                    <div className="flex gap-2">
                      <input
                        defaultValue={editingUser.phone || ''}
                        placeholder="5511999999999"
                        id="edit-phone-input"
                        className="flex-1 bg-slate-50 dark:bg-navy-950 border border-neutral-200 dark:border-navy-700 rounded-xl px-4 py-3 font-bold text-sm dark:text-white outline-none"
                      />
                      <button
                        onClick={async () => {
                          const input = document.getElementById('edit-phone-input') as HTMLInputElement;
                          let phone = input?.value?.replace(/\D/g, '') || '';
                          if (phone && !phone.startsWith('55')) phone = '55' + phone;
                          await supabase.from('profiles').update({ phone: phone || null }).eq('id', editingUser.id);
                          onUpdateUser(editingUser.id, {});
                        }}
                        className="px-4 py-3 bg-emerald-600 text-white rounded-xl text-xs font-black uppercase hover:bg-emerald-700 transition-all"
                      >Salvar</button>
                    </div>
                    <p className="text-[9px] text-neutral-400 ml-1">Formato: 55 + DDD + número. Usado para lembretes de tarefas.</p>
                 </div>

                 {editingUser.role === UserRole.LEADERSHIP && (
                    <div className="p-4 bg-indigo-50 dark:bg-indigo-900/20 rounded-2xl border border-indigo-100 dark:border-indigo-800/30">
                       <div className="flex items-center justify-between">
                          <div>
                             <p className="text-sm font-black text-indigo-900 dark:text-indigo-300">Permitir Edição Própria</p>
                             <p className="text-xs text-indigo-700/70 dark:text-indigo-400 mt-1 max-w-[250px]">
                                Permite que esta liderança edite atendimentos e contatos que ela mesma criou.
                             </p>
                          </div>
                          <button onClick={() => togglePermission('canEditOwnContent')} className={`text-2xl transition-colors ${permissionsForm.canEditOwnContent ? 'text-indigo-600' : 'text-slate-300'}`}>
                             {!!permissionsForm.canEditOwnContent ? <ToggleRight size={32} /> : <ToggleLeft size={32} />}
                          </button>
                       </div>
                    </div>
                 )}

                 {container?.plan === 'betatester' && (
                    <div className="p-4 bg-emerald-50 dark:bg-emerald-900/20 rounded-2xl border border-emerald-100 dark:border-emerald-800/30">
                       <div className="flex items-center justify-between">
                          <div>
                             <p className="text-sm font-black text-emerald-900 dark:text-emerald-300 flex items-center gap-2"><MessageSquare size={16} /> Acesso ao WhatsApp</p>
                             <p className="text-xs text-emerald-700/70 dark:text-emerald-400 mt-1 max-w-[250px]">
                                Permite que este membro acesse o módulo de WhatsApp do gabinete.
                             </p>
                          </div>
                          <button onClick={() => togglePermission('canAccessWhatsApp')} className={`text-2xl transition-colors ${permissionsForm.canAccessWhatsApp ? 'text-emerald-600' : 'text-slate-300'}`}>
                             {!!permissionsForm.canAccessWhatsApp ? <ToggleRight size={32} /> : <ToggleLeft size={32} />}
                          </button>
                       </div>
                    </div>
                 )}

                 <div className="space-y-4">
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Módulos Globais</p>
                    {['canEditCRM', 'canEditAttendances', 'canEditTasks', 'canEditLetters']
                      .filter(key => {
                        if (editingUser.role === UserRole.LEADERSHIP && (key === 'canEditTasks' || key === 'canEditLetters')) {
                          return false;
                        }
                        return true;
                      })
                      .map(key => {
                        let label = '';
                        switch(key) {
                           case 'canEditCRM': label = 'Acesso CRM'; break;
                           case 'canEditAttendances': label = 'Acesso Atendimentos'; break;
                           case 'canEditTasks': label = 'Acesso Tarefas'; break;
                           case 'canEditLetters': label = 'Acesso Documentos'; break;
                           default: label = key;
                        }
                        
                        return (
                           <div key={key} className="flex items-center justify-between p-3 hover:bg-slate-50 dark:hover:bg-navy-950/50 rounded-xl transition-all">
                              <span className="text-xs font-bold text-navy-900 dark:text-white">{label}</span>
                              <button 
                                onClick={() => togglePermission(key as keyof UserPermissions)} 
                                className={`text-2xl transition-colors ${permissionsForm[key as keyof UserPermissions] ? 'text-emerald-500' : 'text-slate-300'}`}
                              >
                                 {!!permissionsForm[key as keyof UserPermissions] ? <ToggleRight size={28} /> : <ToggleLeft size={28} />}
                              </button>
                           </div>
                        );
                    })}
                 </div>

                 <div className="flex justify-end pt-4">
                    <button onClick={handleSavePermissions} disabled={loading} className="w-full bg-navy-950 dark:bg-parliament-600 text-white rounded-xl py-4 font-black text-xs uppercase tracking-widest shadow-lg flex items-center justify-center gap-2">
                       {loading ? <Loader2 className="animate-spin" size={16}/> : <Save size={16} />} Salvar Alterações
                    </button>
                 </div>
              </div>
           </div>
        </div>
      )}

      {/* MODAL DE LOGS */}
      {viewingLogsUserId && (
        <div className="fixed inset-0 z-[130] flex items-center justify-center p-6 bg-navy-950/60 backdrop-blur-sm animate-in fade-in" onClick={() => setViewingLogsUserId(null)}>
           <div className="bg-white dark:bg-navy-900 w-full max-w-lg rounded-[32px] shadow-2xl overflow-hidden border border-neutral-200 dark:border-navy-800 animate-in zoom-in-95" onClick={e => e.stopPropagation()}>
              <div className="p-8 border-b border-neutral-100 dark:border-navy-800 flex justify-between items-center bg-neutral-50 dark:bg-navy-950">
                 <h3 className="text-xl font-black text-navy-950 dark:text-white">Logs de Acesso</h3>
                 <button onClick={() => setViewingLogsUserId(null)} className="p-2 hover:bg-neutral-100 dark:hover:bg-navy-800 rounded-full text-slate-400"><X size={20}/></button>
              </div>
              <div className="p-8 max-h-[60vh] overflow-y-auto custom-scrollbar">
                 <div className="space-y-4">
                    {getUserLogs(viewingLogsUserId).length > 0 ? getUserLogs(viewingLogsUserId).map(log => (
                       <div key={log.id} className="flex items-center gap-4 p-4 bg-neutral-50 dark:bg-navy-950/50 rounded-2xl border border-transparent hover:border-neutral-200 dark:hover:border-navy-700 transition-all">
                          <div className={`p-3 rounded-xl ${log.event_type === 'login' ? 'bg-emerald-100 text-emerald-600 dark:bg-emerald-900/20' : 'bg-red-50 text-red-500 dark:bg-red-900/20'}`}>
                             {log.event_type === 'login' ? <UserCheck size={18} /> : <X size={18} />}
                          </div>
                          <div>
                             <p className="text-xs font-black text-navy-950 dark:text-white uppercase tracking-widest">{log.event_type === 'login' ? 'Entrada no Sistema' : 'Saída do Sistema'}</p>
                             <p className="text-[10px] text-slate-400 font-bold mt-1 flex items-center gap-2">
                                <Calendar size={12}/> {new Date(log.timestamp).toLocaleDateString('pt-BR')}
                                <Clock size={12}/> {new Date(log.timestamp).toLocaleTimeString('pt-BR')}
                             </p>
                          </div>
                       </div>
                    )) : (
                       <div className="text-center py-10">
                          <div className="w-12 h-12 bg-slate-50 dark:bg-navy-800 rounded-full flex items-center justify-center mx-auto mb-3 text-slate-300">
                             <Activity size={24} />
                          </div>
                          <p className="text-slate-400 text-xs font-medium">Nenhum log de sessão encontrado para este usuário.</p>
                       </div>
                    )}
                 </div>
              </div>
           </div>
        </div>
      )}

      {isInvitingMember && (
        <div className="fixed inset-0 z-[130] flex items-center justify-center p-6 bg-navy-950/60 backdrop-blur-sm animate-in fade-in" onClick={() => setIsInvitingMember(false)}>
          <div className="bg-white dark:bg-navy-900 w-full max-w-md rounded-[28px] shadow-2xl overflow-hidden border border-neutral-200 dark:border-navy-800" onClick={e => e.stopPropagation()}>
            <div className="px-8 py-6 border-b border-neutral-100 dark:border-navy-800 flex items-center justify-between bg-neutral-50 dark:bg-navy-950">
              <h3 className="font-black text-navy-950 dark:text-white text-lg">Convidar Membro</h3>
              <button onClick={() => setIsInvitingMember(false)} className="p-2 hover:bg-neutral-200 dark:hover:bg-navy-800 rounded-full text-slate-400"><X size={24} /></button>
            </div>
            <form onSubmit={handleInviteMember} className="p-8 space-y-6">
              <div className="space-y-1">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-2">E-mail do Usuário</label>
                <input required type="email" value={inviteForm.email} onChange={e => setInviteForm({...inviteForm, email: e.target.value})} className="w-full bg-neutral-50 dark:bg-navy-900 border border-neutral-100 dark:border-navy-800 rounded-2xl px-6 py-4 outline-none focus:ring-2 focus:ring-parliament-600 transition-all font-bold dark:text-white" placeholder="email@exemplo.com" />
              </div>
              <div className="space-y-1">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-2">Definir Função</label>
                <select value={inviteForm.role} onChange={e => setInviteForm({...inviteForm, role: e.target.value as UserRole})} className="w-full bg-neutral-50 dark:bg-navy-900 border border-neutral-100 dark:border-navy-800 rounded-2xl px-6 py-4 outline-none font-bold dark:text-white">
                  <option value={UserRole.EDITOR}>Assessor (Gabinete)</option>
                  <option value={UserRole.LEADERSHIP}>Liderança (Externo)</option>
                  <option value={UserRole.ADMIN}>Administrador</option>
                </select>
              </div>
              
              <div className="p-4 bg-parliament-50 dark:bg-navy-950 border border-parliament-100 dark:border-navy-800 rounded-2xl text-[10px] font-bold text-parliament-600 leading-relaxed uppercase">
                O usuário receberá acesso ao seu gabinete assim que criar a conta com este e-mail e aceitar o convite.
              </div>

              <div className="pt-4 flex justify-end gap-3">
                <button type="button" onClick={() => setIsInvitingMember(false)} className="px-6 py-2 text-slate-400 font-bold text-sm">Cancelar</button>
                <button disabled={loading} type="submit" className="px-10 py-4 bg-navy-950 dark:bg-parliament-600 text-white rounded-full font-black text-[10px] uppercase tracking-widest shadow-lg flex items-center gap-2">
                  {loading ? <Loader2 className="animate-spin" size={16} /> : <><Send size={16} /> Enviar Convite</>}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamList;
